/**
 * Platform audit trail — the "who did what" record.
 *
 * This is what the dashboard's recent-activity panel is a preview of: every
 * grant, suspension, elevation, release, export, quota change and *denied*
 * cross-tenant attempt. The table is append-only in the database (a trigger
 * rejects UPDATE and DELETE), so this view cannot be quietly rewritten.
 *
 * It records metadata only — never article text, prompts, tokens or cookies.
 */
import { useLoaderData, useSearchParams } from 'react-router';
import type { AdminAuditEntry, AdminClinic, AdminStore, ListResponse } from '../api.ts';
import { Card, Empty, Pill, Table } from '../components/ui.tsx';
import { Select } from '../components/Select.tsx';

/** Audit actions in the operator's language, grouped by what they affect. */
const ACTION_LABEL: Record<string, string> = {
  'clinic.created': 'クリニック登録',
  'clinic.status_changed': 'クリニック状態変更',
  'clinic.settings_updated': 'クリニック設定更新',
  'store.created': '店舗登録',
  'store.updated': '店舗更新',
  'membership.granted': 'ユーザーを追加',
  'membership.updated': '権限を変更',
  'user.updated': 'ユーザー情報を更新',
  'user.platform_role_granted': 'JMC管理者権限を付与',
  'user.platform_role_revoked': 'JMC管理者権限を解除',
  'membership.revoked': '所属の解除',
  'support.elevation_granted': 'サポート権限昇格',
  'support.elevation_revoked': 'サポート権限解除',
  'prompt.version_created': 'プロンプト版作成',
  'prompt.version_activated': 'プロンプト版有効化',
  'authz.scope_denied': '権限外アクセスを拒否',
  'authz.no_scope': 'クリニック未選択のため拒否',
  'authz.platform_denied': '管理者権限なしを拒否',
  'auth.mfa_required': '多要素認証が未達',
  'auth.step_up': '再認証',
  'auth.step_up_rejected': '再認証を拒否',
  'auth.login': 'ログイン',
  'auth.logout': 'ログアウト',
  'auth.scope_switched': '対象クリニック切替',
  'auth.login_rejected': 'ログイン拒否',
};

const FILTERS = [
  { value: '', label: 'すべての操作' },
  { value: 'support.', label: 'サポート権限昇格' },
  // Narrower than 'authz.' on purpose: this is the one that means someone
  // reached for a clinic that was not theirs.
  { value: 'authz.scope_denied', label: '権限外アクセスのみ' },
  { value: 'authz.', label: '拒否されたアクセス' },
  { value: 'clinic.', label: 'クリニック操作' },
  { value: 'store.', label: '店舗操作' },
  { value: 'membership.', label: 'ユーザーの追加・解除' },
  { value: 'prompt.', label: 'プロンプト' },
];

export default function AdminAudit() {
  const { items, clinics, stores } = useLoaderData<
    ListResponse<AdminAuditEntry> & { clinics: AdminClinic[]; stores: AdminStore[] }
  >();
  const [params, setParams] = useSearchParams();

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // A store belongs to one clinic: changing the clinic drops the store.
    if (key === 'clinicId') next.delete('storeId');
    setParams(next);
  }

  return (
    <Card
      title="監査ログ"
      caption="権限・テナント・リリースに関する操作の記録です。追記のみで、後から書き換えられません。"
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        {/*
          クリニック, then 店舗 within it. The clinic filter existed in the API
          and the URL but the screen offered no way to set it - it could only be
          reached from a link on another page.
        */}
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="audit-clinic">クリニック</label>
          <div style={{ width: 220 }}>
            <Select
              id="audit-clinic"
              value={params.get('clinicId') ?? ''}
              onChange={(next) => setParam('clinicId', next)}
              options={[
                { value: '', label: 'すべてのクリニック' },
                ...clinics.map((clinic) => ({ value: clinic.id, label: clinic.name })),
              ]}
            />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="audit-store">店舗</label>
          <div style={{ width: 200 }}>
            <Select
              id="audit-store"
              value={params.get('storeId') ?? ''}
              onChange={(next) => setParam('storeId', next)}
              disabled={!params.get('clinicId')}
              options={[
                {
                  value: '',
                  label: params.get('clinicId') ? 'すべての店舗' : 'クリニックを選ぶと選べます',
                },
                ...stores.map((store) => ({ value: store.id, label: store.name })),
              ]}
            />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="action">操作の種類</label>
          <div style={{ width: 220 }}>
            <Select
              id="action"
              value={params.get('action') ?? ''}
              onChange={(next) => setParam('action', next)}
              options={FILTERS.map((filter) => ({ value: filter.value, label: filter.label }))}
            />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="outcome">結果</label>
          <div style={{ width: 160 }}>
            <Select
              id="outcome"
              value={params.get('outcome') ?? ''}
              onChange={(next) => setParam('outcome', next)}
              options={[
                { value: '', label: 'すべて' },
                { value: 'SUCCESS', label: '成功' },
                { value: 'DENIED', label: '拒否' },
                { value: 'FAILURE', label: '失敗' },
              ]}
            />
          </div>
        </div>
        {(params.get('clinicId') || params.get('action') || params.get('outcome')) && (
          <div style={{ alignSelf: 'end' }}>
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => setParams(new URLSearchParams())}>
              絞り込みを解除
            </button>
          </div>
        )}
      </div>

      {params.get('storeId') && (
        <p className="muted small" style={{ marginTop: 0 }}>
          店舗の絞り込みは、店舗を対象にした操作と、その店舗で作業中に行われた操作が対象です。
          この機能の追加より前の記録は、店舗そのものを対象にした操作だけが該当します。
        </p>
      )}

      {items.length === 0 ? (
        <Empty>条件に一致する記録がありません。</Empty>
      ) : (
        <Table
          caption={`${items.length} 件を表示しています。`}
          head={
            <>
              <th scope="col">日時</th>
              <th scope="col">操作</th>
              <th scope="col">実行者</th>
              <th scope="col">対象クリニック</th>
              <th scope="col">店舗</th>
              <th scope="col">結果</th>
            </>
          }
        >
          {items.map((entry) => (
            <tr key={entry.id}>
              <td style={{ whiteSpace: 'nowrap' }}>
                {new Date(entry.created_at).toLocaleString('ja-JP')}
              </td>
              <td>
                {ACTION_LABEL[entry.action] ?? entry.action}
                <span className="muted small" style={{ display: 'block' }}>
                  {entry.action}
                </span>
              </td>
              <td className="muted">{entry.actor_email ?? 'システム'}</td>
              <td>{entry.clinic_name ?? '—'}</td>
              <td className="muted">{entry.store_name ?? '—'}</td>
              <td>
                <Pill
                  tone={
                    entry.outcome === 'DENIED'
                      ? 'danger'
                      : entry.outcome === 'FAILURE'
                        ? 'warn'
                        : 'ok'
                  }
                >
                  {entry.outcome}
                </Pill>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}
